// AbstractShape shader inspired by AbstractShapeBg
// Approximates canvas abstract shapes with seeded Voronoi regions,
// subtle wavy warp and controllable grain noise

const frag = `
precision mediump float;

uniform vec2 u_resolution;
uniform float u_time;
uniform float u_speed;
uniform float u_seed;

uniform float u_wavy;          // 0.0 - 30.0 recommended
uniform float u_noise_amount;  // 0.0 - 0.5 recommended

uniform vec3 u_color_0;
uniform vec3 u_color_1;
uniform vec3 u_color_2;
uniform vec3 u_color_3;
uniform vec3 u_color_4;
uniform vec3 u_color_5;

varying vec2 vUv;

float rand(vec2 n) {
  return fract(sin(dot(n, vec2(12.9898, 78.233)) + u_seed * 19.19) * 43758.5453);
}

// Hash to generate N seed points deterministically from u_seed
vec2 seedPoint(float i) {
  float a = rand(vec2(i + 0.123, u_seed*0.017));
  float b = rand(vec2(i + 0.789, u_seed*0.071));
  return vec2(a, b);
}

vec3 paletteColor(int idx) {
  if (idx == 0) return u_color_0;
  if (idx == 1) return u_color_1;
  if (idx == 2) return u_color_2;
  if (idx == 3) return u_color_3;
  if (idx == 4) return u_color_4;
  return u_color_5;
}

void main() {
  // UV and gentle wavy warp (scaled to keep effect subtle)
  vec2 uv = gl_FragCoord.xy / u_resolution.xy;
  float amp = u_wavy * 0.003; // scale down for fragment-space warp
  vec2 wavyCoord;
  float t = u_time * u_speed;
  wavyCoord.x = uv.x + sin(t + uv.y * 15.0) * amp;
  wavyCoord.y = uv.y + cos(t + uv.x * 15.0) * amp;

  // Voronoi-like partition with 6 seeded sites
  float minD = 10.0;
  int minI = 0;
  for (int i = 0; i < 6; i++) {
    vec2 p = seedPoint(float(i));
    float d = distance(wavyCoord, p);
    if (d < minD) { minD = d; minI = i; }
  }

  // Base color from nearest region; add soft mixing to neighbor regions for more organic edges
  vec3 col = paletteColor(minI);

  // Edge softening using a simple smoothstep rim around the closest site
  float edge = smoothstep(0.35, 0.0, minD);
  col = mix(col, vec3(1.0), edge * 0.03);

  // Subtle background gradient to add depth
  vec3 grad = mix(u_color_0, u_color_1, wavyCoord.x);
  col = mix(grad, col, 0.85);

  // Film grain-like noise
  float n = (rand(wavyCoord + vec2(t * 0.013, u_seed * 0.007)) - 0.5) * u_noise_amount;
  col += col * n;

  gl_FragColor = vec4(col, 1.0);
}
`;

const shader = {
  name: 'AbstractShape',
  shader: {
    frag,
  },
  schema: [
    {
      key: 'colors',
      type: 'color-palette',
      label: 'Colors',
      min: 2,
      max: 6,
      default: ['#f5af19', '#f12711', '#ffffff'],
    },
    { key: 'u_wavy', type: 'number', label: 'Wavy', min: 0, max: 30, step: 0.5, default: 10 },
    {
      key: 'u_noise_amount',
      type: 'number',
      label: 'Noise',
      min: 0,
      max: 0.5,
      step: 0.01,
      default: 0.05,
    },
    { key: 'u_seed', type: 'number', label: 'Seed', min: 0, max: 9999, step: 1, default: 0 },
    { key: 'u_speed', type: 'number', label: 'Speed', min: 0, max: 10, step: 0.1, default: 1 },
    { key: 'animated', type: 'boolean', label: 'Animated', default: true },
  ],
  examples: [
    'dynamic-shader(AbstractShape, colors=#f5af19,#f12711,#fff; u_wavy=10; u_noise_amount=0.05; animated=false)',
    'dynamic-shader(AbstractShape, colors=#00c6ff,#0072ff,#222; u_wavy=6; u_noise_amount=0.03; animated=false)',
    'dynamic-shader(AbstractShape, colors=#ff9a9e,#fad0c4; u_wavy=0; u_noise_amount=0.02; animated=false)',
  ],
};

export default shader;
